import routes from './routes';
import {createRouter, createWebHistory} from 'vue-router';
import store from '@store';
import permissionService from '@services/permissionService';
import {useMessageStore} from '../store/messagesStore';
import {useAxios} from '../store/axiosStore';
import {Crisp} from 'crisp-sdk-web';

const router = createRouter({
  routes,
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      return {selector: to.hash};
    } else {
      return {x: 0, y: 0};
    }
  },
});

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom) => {
  // Check if auth is required on this route
  // (including nested routes).
  //CANCEL ALL AXIOS REQUEST ON PREVIOUS PAGE
  const requestsStore = useAxios();
  if (requestsStore.getPage(routeFrom.path)) {
    for (let instance of requestsStore.getPage(routeFrom.path)) {
      instance.abort();
    }
    requestsStore.deletePage(routeFrom.path);
  }
  const messageStore = useMessageStore();
  messageStore.$reset();
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);

  // If auth isn't required for the route, just continue.
  if (!authRequired) return true;

  // If auth is required and the user is logged in...
  if (store.getters['loggedIn']) {
    // Validate the local user token...
    store.dispatch('validate').then((validUser) => {
      // Then continue if the token still represents a valid user,
      // otherwise redirect to login.
      if (!validUser) return redirectToLogin();
    });
  } else {
    return redirectToLogin();
  }
  const accessTo = routeTo.meta?.accessTo;
  const restrictedTo = routeTo.meta?.restrictedTo;
  const permissions = routeTo.meta?.permissions;

  // If the route has no particular restrictions, just continue
  if (!accessTo && !restrictedTo && !permissions) return true;

  // Check the route restrictions, if the user isn't allowed, redirect to the unauthorized page
  if (accessTo && !permissionService.isOneOfThem(accessTo)) return redirectToUnauthorized();
  if (restrictedTo && permissionService.isOneOfThem(restrictedTo)) return redirectToUnauthorized();
  if (permissions && !permissionService.hasOneOfThem(permissions)) return redirectToUnauthorized();

  function redirectToLogin() {
    // Pass the original route to the login component
    return {name: 'login', query: {redirectFrom: routeTo.fullPath}};
  }

  function redirectToUnauthorized() {
    // Pass the original route to the login component
    return {name: 'unauthorized'};
  }

  // If we reach this point, continue resolving the route.
  return true;
});

router.beforeResolve(async (routeTo, routeFrom) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      // If a `beforeResolve` hook is defined, call it with
      // the same arguments as the `beforeEnter` hook.
      if (route.meta && route.meta.beforeResolve) {
        const result = await route.meta.beforeResolve(routeTo, routeFrom);
        // If the guard returned something, let the router handle it
        if (result) return result;
      }
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }

  // If we reach this point, continue resolving the route.
  return true;
});

router.afterEach((to) => {
  document.body.classList.remove('public-page');

  if (to.meta.createPage) {
    document.body.classList.add('create-page');
  }
  if (to.meta.disableCrisp) {
    console.log('On doit cacher Crisp');
    if (Crisp.isCrispInjected()) {
      console.log('On cache');
      Crisp.chat.hide();
    }
  }
});
export default router;
